/**
 * KinsCircle Upload Reliability Layer V1 — Centralized Upload Queue
 *
 * A small, in-memory + localStorage-persisted queue manager that supports
 * sequential uploads, pause / resume / cancel / retry, hard timeout
 * watchdogs, exponential-backoff auto-retry, and survives route changes.
 *
 * STRICTLY ADDITIVE — opt-in only. The locked legacy upload pipelines
 * (`MediaUploadModal`, `PhotoUpload`, `EditProfileModal`,
 * `FamilyContext.uploadFamilyPhoto`, `MessageBoard` upload) continue to
 * use their existing direct `supabase.storage.from('family-media').upload()`
 * calls and are NEVER routed through this queue. Future callers that
 * want resilience features call `enqueueUpload()` explicitly.
 *
 * Persistence:
 *   - Pending / in-flight metadata is mirrored into localStorage so a
 *     refresh / navigation can RESTORE the queue UI ("upload was
 *     interrupted — retry").
 *   - The File bytes themselves are NOT persisted (browsers do not let
 *     us serialize a File). On restore we surface a "ready to retry"
 *     state and the user re-attaches the original file. This avoids
 *     silent data loss.
 *   - Completed entries are removed cleanly.
 *
 * Honors STABILITY.md §1, §3, §4, §5, §7.5, §10.
 */

import {
  supabaseUploadAdapter,
  UploadTransportError,
  type UploadAdapter,
} from '@/lib/uploads/uploadAdapter';
import { isUploadReliabilityEnabled } from '@/lib/uploads/featureFlag';
import {
  logUploadStart,
  logUploadProgress,
  logUploadTimeout,
  logUploadRetry,
  logUploadSuccess,
  logUploadFailure,
} from '@/lib/stabilityLogger';
import {
  fingerprintFile,
  type KnownUploadFingerprint,
} from '@/lib/uploads/uploadValidation';

export type UploadStatus =
  | 'queued'
  | 'uploading'
  | 'retrying'
  | 'paused'
  | 'failed'
  | 'completed'
  | 'restoring';

export interface UploadQueueItem {
  id: string;
  /** When the item was enqueued (ms). */
  createdAt: number;
  /** Last status transition timestamp (ms). */
  updatedAt: number;
  /** Stable, non-PII label shown in the recovery card. */
  label: string;
  /** Original filename — shown back to the user. */
  fileName: string;
  /** Byte size at enqueue time. */
  fileSize: number;
  /** Storage object path. */
  storagePath: string;
  /** image | video | other (free string for caller-defined kinds). */
  kind: string;
  /** Current status. */
  status: UploadStatus;
  /** 0–100. */
  progress: number;
  /** How many attempts have been made (1-based at first transmit). */
  attempt: number;
  /** Maximum attempts before giving up. */
  maxAttempts: number;
  /** When the most recent attempt started (ms). */
  attemptStartedAt: number | null;
  /** When the most recent attempt finished (ms). Used for diagnostics. */
  attemptEndedAt: number | null;
  /** Total wall-clock duration of the last completed attempt (ms). */
  lastDurationMs: number | null;
  /** Last error code, if any. */
  errorCode: string | null;
  /** Last user-safe error message, if any. */
  errorMessage: string | null;
  /** Public URL after a successful upload, if known. */
  publicUrl: string | null;
  /** Number of timeout watchdog firings. */
  timeoutFires: number;
  /** Whether this entry was hydrated from localStorage on app boot. */
  restored: boolean;
}

export interface EnqueueRequest {
  /** Storage object path. */
  storagePath: string;
  /** The file bytes. */
  file: File;
  /** image | video | other free-form kind. */
  kind: string;
  /** Optional friendly label (defaults to filename). */
  label?: string;
  /** Hard timeout per attempt, in ms. Defaults to 60s for image, 180s for video. */
  timeoutMs?: number;
  /** Maximum attempts. Defaults to 3 (1 initial + 2 retries). */
  maxAttempts?: number;
  /** Optional caller-supplied id (idempotency); auto-generated otherwise. */
  externalId?: string;
}

export interface EnqueueResult {
  ok: boolean;
  id?: string;
  /** When `ok=false`, why the queue refused to accept the upload. */
  reason?: 'flag-disabled' | 'duplicate-in-flight' | 'invalid';
}

interface InternalEntry extends UploadQueueItem {
  /** Live in-memory File handle — NOT persisted to localStorage. */
  file: File | null;
  /** Abort controller for the in-flight transport call. */
  abort: AbortController | null;
  /** Watchdog timer id. */
  watchdog: number | null;
}

type Listener = (snapshot: UploadQueueItem[]) => void;

const STORE_KEY = 'kinscircle.upload.queue.v1';
const FINGERPRINT_KEY = 'kinscircle.upload.fingerprints.v1';
const FINGERPRINT_CAP = 60;
const MAX_PERSISTED = 25;
const DEFAULT_IMG_TIMEOUT = 60_000;
const DEFAULT_VID_TIMEOUT = 180_000;
const DEFAULT_MAX_ATTEMPTS = 3;

// ---------------------------------------------------------------------------
// In-memory queue state. Module-scoped so multiple components share one
// queue instance — survives route changes because routes don't unmount
// the module.
// ---------------------------------------------------------------------------

const entries: Map<string, InternalEntry> = new Map();
const listeners = new Set<Listener>();
let processing = false;

const adapter: UploadAdapter = supabaseUploadAdapter;

const safeStorage = {
  read: (key: string): string | null => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return null;
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  write: (key: string, value: string): void => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      window.localStorage.setItem(key, value);
    } catch {
      /* non-fatal */
    }
  },
};

const persistSnapshot = (): void => {
  try {
    const list: UploadQueueItem[] = [];
    entries.forEach((e) => {
      // Strip non-serializable fields. Files are not persisted.
      const { file: _f, abort: _a, watchdog: _w, ...rest } = e;
      list.push({ ...rest });
    });
    safeStorage.write(STORE_KEY, JSON.stringify(list.slice(0, MAX_PERSISTED)));
  } catch {
    /* non-fatal */
  }
};

const notify = (): void => {
  const snapshot = getQueueSnapshot();
  listeners.forEach((l) => {
    try { l(snapshot); } catch { /* listener crashes never break the queue */ }
  });
  persistSnapshot();
};

const setItem = (id: string, patch: Partial<InternalEntry>): void => {
  const cur = entries.get(id);
  if (!cur) return;
  const next: InternalEntry = { ...cur, ...patch, updatedAt: Date.now() };
  entries.set(id, next);
  notify();
};

const newId = () => `up-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const defaultTimeoutFor = (kind: string): number =>
  kind === 'video' ? DEFAULT_VID_TIMEOUT : DEFAULT_IMG_TIMEOUT;

// Exponential backoff: 1.5s → 4s → 9s, capped at 12s.
const backoffMs = (attempt: number): number => {
  const ms = Math.min(12_000, 1500 * Math.pow(2.2, attempt - 1));
  return Math.round(ms);
};

const sleep = (ms: number) => new Promise<void>((res) => window.setTimeout(res, ms));

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const enqueueUpload = (req: EnqueueRequest): EnqueueResult => {
  if (!isUploadReliabilityEnabled()) {
    return { ok: false, reason: 'flag-disabled' };
  }
  if (!req || !req.file || !req.storagePath) {
    return { ok: false, reason: 'invalid' };
  }
  // Prevent duplicate simultaneous uploads of the same in-flight file.
  for (const existing of entries.values()) {
    if (
      existing.fileName === req.file.name &&
      existing.fileSize === req.file.size &&
      (existing.status === 'queued' ||
        existing.status === 'uploading' ||
        existing.status === 'retrying')
    ) {
      return { ok: false, reason: 'duplicate-in-flight', id: existing.id };
    }
  }
  const id = req.externalId || newId();
  const now = Date.now();
  const entry: InternalEntry = {
    id,
    createdAt: now,
    updatedAt: now,
    label: req.label || req.file.name || 'Upload',
    fileName: req.file.name || 'file',
    fileSize: req.file.size,
    storagePath: req.storagePath,
    kind: req.kind || 'image',
    status: 'queued',
    progress: 0,
    attempt: 0,
    maxAttempts: req.maxAttempts ?? DEFAULT_MAX_ATTEMPTS,
    attemptStartedAt: null,
    attemptEndedAt: null,
    lastDurationMs: null,
    errorCode: null,
    errorMessage: null,
    publicUrl: null,
    timeoutFires: 0,
    restored: false,
    file: req.file,
    abort: null,
    watchdog: null,
  };
  entries.set(id, entry);
  notify();
  // Kick off processing. Errors here never throw to the caller —
  // the queue manager runs on its own clock.
  void processQueue(req.timeoutMs);
  return { ok: true, id };
};

export const pauseUpload = (id: string): void => {
  const e = entries.get(id);
  if (!e) return;
  if (e.status === 'uploading' || e.status === 'retrying') {
    e.abort?.abort();
  }
  if (e.watchdog) {
    window.clearTimeout(e.watchdog);
  }
  setItem(id, { status: 'paused', abort: null, watchdog: null });
};

export const resumeUpload = (id: string, timeoutMs?: number): void => {
  const e = entries.get(id);
  if (!e || e.status !== 'paused') return;
  setItem(id, { status: 'queued' });
  void processQueue(timeoutMs);
};

export const cancelUpload = (id: string): void => {
  const e = entries.get(id);
  if (!e) return;
  e.abort?.abort();
  if (e.watchdog) window.clearTimeout(e.watchdog);
  entries.delete(id);
  notify();
};

export const retryUpload = (id: string, timeoutMs?: number): void => {
  const e = entries.get(id);
  if (!e) return;
  if (!e.file) {
    // File handle is gone (e.g. restored from localStorage after refresh).
    // We cannot retry without re-attaching — mark as awaiting re-attach.
    setItem(id, {
      status: 'restoring',
      errorCode: 'restoring',
      errorMessage: 'Please re-select this file to continue the upload.',
    });
    return;
  }
  setItem(id, {
    status: 'queued',
    progress: 0,
    errorCode: null,
    errorMessage: null,
  });
  void processQueue(timeoutMs);
};

/**
 * Re-attach a fresh File handle to a restored queue item so the user
 * can finish an upload that was interrupted by a refresh / navigation.
 * The fingerprint must match (filename + size) — otherwise the call is
 * rejected so the user does not accidentally upload the wrong file
 * under an existing id.
 */
export const reattachFile = (id: string, file: File): boolean => {
  const e = entries.get(id);
  if (!e) return false;
  if (e.fileName !== file.name || e.fileSize !== file.size) return false;
  e.file = file;
  setItem(id, {
    status: 'queued',
    errorCode: null,
    errorMessage: null,
  });
  void processQueue();
  return true;
};

export const removeCompleted = (): void => {
  const ids: string[] = [];
  entries.forEach((e, id) => {
    if (e.status === 'completed') ids.push(id);
  });
  ids.forEach((id) => entries.delete(id));
  if (ids.length > 0) notify();
};

export const getQueueSnapshot = (): UploadQueueItem[] => {
  const list: UploadQueueItem[] = [];
  entries.forEach((e) => {
    const { file: _f, abort: _a, watchdog: _w, ...rest } = e;
    list.push({ ...rest });
  });
  list.sort((a, b) => a.createdAt - b.createdAt);
  return list;
};

export const subscribeQueue = (l: Listener): (() => void) => {
  listeners.add(l);
  // Push initial snapshot synchronously so the subscriber paints.
  try { l(getQueueSnapshot()); } catch { /* swallow */ }
  return () => { listeners.delete(l); };
};

// ---------------------------------------------------------------------------
// Restore from localStorage on first import. Items come back in
// `restoring` status so the UI can prompt the user to re-attach the file.
// ---------------------------------------------------------------------------
const restoreFromStorage = () => {
  if (!isUploadReliabilityEnabled()) return;
  if (typeof window === 'undefined') return;
  try {
    const raw = safeStorage.read(STORE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as UploadQueueItem[];
    if (!Array.isArray(parsed)) return;
    parsed.forEach((it) => {
      // Anything that was completed before the refresh stays completed;
      // pending / in-flight items become 'restoring' so the user can
      // re-attach. We never mark a stale entry as a hard failure during
      // restore — that would leak a non-actionable error to the user.
      const status: UploadStatus =
        it.status === 'completed' ? 'completed' : 'restoring';
      const e: InternalEntry = {
        ...it,
        status,
        progress: it.status === 'completed' ? 100 : it.progress,
        errorCode: status === 'restoring' ? 'restoring' : it.errorCode ?? null,
        errorMessage:
          status === 'restoring'
            ? 'Please re-select this file to continue the upload.'
            : it.errorMessage ?? null,
        restored: true,
        file: null,
        abort: null,
        watchdog: null,
      };
      entries.set(it.id, e);
    });
    notify();
  } catch {
    /* non-fatal — ignore corrupt persisted state */
  }
};
restoreFromStorage();

// ---------------------------------------------------------------------------
// Fingerprint store — used by callers (e.g. MediaUploadModal future
// integration) to surface "this file may already exist" warnings.
// ---------------------------------------------------------------------------
export const recordFingerprint = (file: File, memoryId?: string | null): void => {
  try {
    const raw = safeStorage.read(FINGERPRINT_KEY);
    const parsed = (raw ? JSON.parse(raw) : []) as KnownUploadFingerprint[];
    const list = Array.isArray(parsed) ? parsed : [];
    list.unshift(fingerprintFile(file, memoryId));
    safeStorage.write(FINGERPRINT_KEY, JSON.stringify(list.slice(0, FINGERPRINT_CAP)));
  } catch {
    /* non-fatal */
  }
};

export const readFingerprints = (): KnownUploadFingerprint[] => {
  try {
    const raw = safeStorage.read(FINGERPRINT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as KnownUploadFingerprint[]) : [];
  } catch {
    return [];
  }
};

// ---------------------------------------------------------------------------
// Core processor. Runs sequentially through queued items, applying the
// timeout watchdog, exponential backoff, and stability logging hooks.
// ---------------------------------------------------------------------------

async function processQueue(timeoutOverride?: number): Promise<void> {
  if (processing) return;
  processing = true;
  try {
    while (true) {
      const next = pickNextQueued();
      if (!next) break;
      await runOne(next.id, timeoutOverride);
    }
  } finally {
    processing = false;
  }
}

const pickNextQueued = (): InternalEntry | null => {
  for (const e of entries.values()) {
    if (e.status === 'queued' && e.file) return e;
  }
  return null;
};

async function runOne(id: string, timeoutOverride?: number): Promise<void> {
  const entry = entries.get(id);
  if (!entry || !entry.file) return;
  const timeoutMs = timeoutOverride ?? defaultTimeoutFor(entry.kind);

  while (entry.attempt < entry.maxAttempts) {
    entry.attempt += 1;
    const startedAt = Date.now();
    const abort = new AbortController();
    entry.abort = abort;

    setItem(id, {
      status: entry.attempt === 1 ? 'uploading' : 'retrying',
      attemptStartedAt: startedAt,
      attemptEndedAt: null,
      progress: 5,
      errorCode: null,
      errorMessage: null,
    });

    if (entry.attempt === 1) {
      logUploadStart({
        message: 'upload begin',
        context: { id, kind: entry.kind, size: entry.fileSize },
      });
    } else {
      logUploadRetry({
        message: 'upload retry',
        context: { id, attempt: entry.attempt, kind: entry.kind },
      });
    }

    // Synthetic progress climb — real Supabase JS upload doesn't expose
    // streaming progress, so we mirror the same 8→90 ramp the legacy
    // MediaUploadModal uses and let the queue's `completed` step bump
    // it to 100. Keeps the UX honest about phases.
    const tick = window.setInterval(() => {
      const cur = entries.get(id);
      if (!cur || (cur.status !== 'uploading' && cur.status !== 'retrying')) return;
      const next = Math.min(90, cur.progress + Math.max(2, (90 - cur.progress) * 0.08));
      setItem(id, { progress: next });
    }, 220);

    // Timeout watchdog — surfaces "upload taking longer than expected"
    // and aborts the in-flight transport call so the user sees a Retry
    // affordance instead of an infinite spinner.
    const watchdog = window.setTimeout(() => {
      const cur = entries.get(id);
      if (!cur) return;
      if (cur.status === 'uploading' || cur.status === 'retrying') {
        abort.abort();
        setItem(id, { timeoutFires: cur.timeoutFires + 1 });
        logUploadTimeout({
          message: 'upload watchdog timeout',
          context: { id, attempt: entry.attempt, timeoutMs },
        });
      }
    }, timeoutMs);
    entry.watchdog = watchdog as unknown as number;

    try {
      const result = await adapter.upload({
        path: entry.storagePath,
        file: entry.file,
        signal: abort.signal,
      });
      window.clearInterval(tick);
      window.clearTimeout(watchdog);
      const durationMs = Date.now() - startedAt;
      setItem(id, {
        status: 'completed',
        progress: 100,
        attemptEndedAt: Date.now(),
        lastDurationMs: durationMs,
        publicUrl: result.publicUrl,
        errorCode: null,
        errorMessage: null,
      });
      logUploadSuccess({
        message: 'upload complete',
        context: { id, attempt: entry.attempt, durationMs },
      });
      // Record fingerprint so future enqueues can warn on duplicates.
      try { recordFingerprint(entry.file, null); } catch { /* non-fatal */ }
      logUploadProgress({
        message: 'upload finished',
        context: { id, progress: 100 },
      });
      return;
    } catch (err) {
      window.clearInterval(tick);
      window.clearTimeout(watchdog);
      const classified =
        err instanceof UploadTransportError
          ? err.classified
          : { code: 'unknown' as const, message: 'Upload failed.', retryable: true };

      setItem(id, {
        attemptEndedAt: Date.now(),
        lastDurationMs: Date.now() - startedAt,
        errorCode: classified.code,
        errorMessage: classified.message,
      });

      // Aborted = user cancelled or queue was paused. Don't auto-retry.
      if (classified.code === 'aborted') {
        logUploadFailure({
          message: 'upload aborted',
          context: { id, attempt: entry.attempt },
        });
        // If still in our map, leave status as paused (set by pauseUpload)
        // unless the entry was cancelled (then it's already deleted).
        const after = entries.get(id);
        if (after && after.status !== 'paused') {
          setItem(id, { status: 'failed' });
        }
        return;
      }

      const canRetry = classified.retryable && entry.attempt < entry.maxAttempts;
      if (!canRetry) {
        setItem(id, { status: 'failed', progress: 0 });
        logUploadFailure({
          message: 'upload failed (no retry)',
          context: { id, attempt: entry.attempt, code: classified.code },
        });
        return;
      }

      // Backoff before next attempt.
      const wait = backoffMs(entry.attempt);
      logUploadRetry({
        message: 'upload retry scheduled',
        context: { id, attempt: entry.attempt + 1, waitMs: wait, code: classified.code },
      });
      await sleep(wait);
      // Loop continues — next attempt fires.
    }
  }
}

/**
 * Hard reset — clears all in-memory + persisted queue state. Used by
 * `/admin/health` diagnostics to recover from a corrupt state without
 * touching real Supabase rows. Never deletes any uploaded media.
 */
export const resetQueue = (): void => {
  entries.forEach((e) => {
    try { e.abort?.abort(); } catch { /* swallow */ }
    if (e.watchdog) {
      try { window.clearTimeout(e.watchdog); } catch { /* swallow */ }
    }
  });
  entries.clear();
  try { safeStorage.write(STORE_KEY, '[]'); } catch { /* swallow */ }
  notify();
};
